import { AttendanceStatusEnum } from "@sinch/core/entities/serverEnums";

/**
 * Status bucket of an attendance. Invited means the staff member has not accepted
 * the shift yet (`CONFIRMATION_PENDING`), every other status counts as confirmed,
 * the same rule the confirmation request uses on the Job detail.
 */
export enum AudienceStatus {
  Confirmed = "confirmed",
  Invited = "invited",
}

/**
 * Whether the position of an attendance is still active or was cancelled.
 */
export enum AudiencePositions {
  Active = "active",
  Cancelled = "cancelled",
}

/**
 * One attendance of the job reduced to what the audience filter needs.
 */
export interface AudienceAttendance {
  userId: string;
  status: AudienceStatus;
  positions: AudiencePositions;
}

/**
 * Selected options of both toggle groups. An attendance matches when it is in
 * one of the selected statuses AND on one of the selected position kinds.
 */
export interface AudienceSelection {
  statuses: AudienceStatus[];
  positions: AudiencePositions[];
}

/**
 * Per-person breakdown, used to warn about people whose attendances on the job
 * fall into different buckets (e.g. invited on Saturday, confirmed on Sunday).
 */
export interface AudienceUserSummary {
  total: number;
  invited: number;
  cancelled: number;
}

export const FULL_AUDIENCE_SELECTION: AudienceSelection = {
  statuses: [AudienceStatus.Confirmed, AudienceStatus.Invited],
  positions: [AudiencePositions.Active, AudiencePositions.Cancelled],
};

/**
 * Maps an attendance status from the API to its audience bucket.
 * A missing status is not an invitation, so it counts as confirmed.
 */
export const toAudienceStatus = (status: `${AttendanceStatusEnum}` | null | undefined): AudienceStatus =>
  status === AttendanceStatusEnum.ConfirmationPending ? AudienceStatus.Invited : AudienceStatus.Confirmed;

export const matchesAudience = (attendance: AudienceAttendance, selection: AudienceSelection): boolean =>
  selection.statuses.includes(attendance.status) && selection.positions.includes(attendance.positions);

/**
 * Distinct users having at least one attendance matching the selection,
 * in the order of their first matching attendance.
 */
export const selectAudienceUserIds = (attendances: AudienceAttendance[], selection: AudienceSelection): string[] => [
  ...new Set(attendances.filter((attendance) => matchesAudience(attendance, selection)).map(({ userId }) => userId)),
];

/**
 * Replaces the job's people in the current recipients with the selected audience.
 * Recipients that are not on the job (added by hand) are kept untouched and the
 * order of the recipients that stay is preserved.
 */
export const applyAudience = (
  currentRecipients: string[],
  attendances: AudienceAttendance[],
  selection: AudienceSelection
): string[] => {
  const jobUserIds = new Set(attendances.map(({ userId }) => userId));
  const audienceUserIds = selectAudienceUserIds(attendances, selection);
  const audience = new Set(audienceUserIds);
  const kept = currentRecipients.filter((id) => !jobUserIds.has(id) || audience.has(id));
  const keptSet = new Set(kept);

  return [...kept, ...audienceUserIds.filter((id) => !keptSet.has(id))];
};

export const summarizeAudienceUsers = (attendances: AudienceAttendance[]): Map<string, AudienceUserSummary> =>
  attendances.reduce((summaries, { userId, status, positions }) => {
    const summary = summaries.get(userId) ?? { total: 0, invited: 0, cancelled: 0 };
    summaries.set(userId, {
      total: summary.total + 1,
      invited: summary.invited + (status === AudienceStatus.Invited ? 1 : 0),
      cancelled: summary.cancelled + (positions === AudiencePositions.Cancelled ? 1 : 0),
    });
    return summaries;
  }, new Map<string, AudienceUserSummary>());

/**
 * True when the person's attendances do not all share the same status, or do not
 * all share the same position kind, so a single toggle does not describe them.
 */
export const isMixedAudienceUser = ({ total, invited, cancelled }: AudienceUserSummary): boolean =>
  (invited > 0 && invited < total) || (cancelled > 0 && cancelled < total);
